/**
 * Cashier Dashboard
 * 
 * Interface for cashier role to view and manage transactions for their outlet.
 * Displays outlet-filtered transactions and allows creating new transactions.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { useOutletAuth } from '../../lib/auth/OutletAuthProvider';
import { LogoutModal } from '../../components/LogoutModal';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../../lib/config/terminal-urls';
import type { Transaction, Product, CustomerInfo } from '@photobooth/types';

const apiClient = createPhotoBoothAPI({
  baseUrl: getEffectiveApiBaseUrl(),
});

/** Copy icon button for transaction ID; shows tooltip and checkmark feedback after copy */
function CopyTransactionIdButton({ onClick }: { onClick: () => void | Promise<void> }) {
  const [copied, setCopied] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await onClick();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center justify-center w-7 h-7 rounded text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-colors ml-1 align-middle"
      title={copied ? 'Copied!' : 'Copy transaction ID'}
      aria-label={copied ? 'Copied!' : 'Copy transaction ID'}
    >
      {copied ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
        </svg>
      )}
    </button>
  );
}

export default function CashierDashboard() {
  const { authState, outlet, clearAuthentication } = useOutletAuth();
  
  // Set auth token on API client when component mounts or auth state changes
  useEffect(() => {
    try {
      const cachedToken = localStorage.getItem('photobooth_auth_token');
      if (cachedToken) {
        apiClient.setAuthToken(cachedToken);
      }
    } catch {
      // Non-critical: if storage is unavailable, requests will just be unauthenticated
    }
  }, [authState]);
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewTransactionModal, setShowNewTransactionModal] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [isCreatingTransaction, setIsCreatingTransaction] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [markingPrintDoneId, setMarkingPrintDoneId] = useState<string | null>(null);
  const [qrModalTransactionId, setQrModalTransactionId] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('');
  const [captureStatusFilter, setCaptureStatusFilter] = useState<string>('');
  const [editStatusFilter, setEditStatusFilter] = useState<string>('');
  const [printStatusFilter, setPrintStatusFilter] = useState<string>('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showAddAddonsModal, setShowAddAddonsModal] = useState(false);
  const [addAddonsTransaction, setAddAddonsTransaction] = useState<Transaction | null>(null);
  const [isAddingAddons, setIsAddingAddons] = useState(false);

  const fetchTransactions = useCallback(async () => {
    if (!authState?.outletId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.transactions.getByOutlet(authState.outletId, {
        search: search || undefined,
        paymentStatus: paymentStatusFilter || undefined,
        captureStatus: captureStatusFilter || undefined,
        editStatus: editStatusFilter || undefined,
        printStatus: printStatusFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (response.success && response.data) {
        setTransactions(response.data);
      } else {
        setError(response.error?.message || 'Failed to fetch transactions');
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to fetch transactions');
    } finally {
      setIsLoading(false);
    }
  }, [
    authState?.outletId,
    search,
    paymentStatusFilter,
    captureStatusFilter,
    editStatusFilter,
    printStatusFilter,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    if (!qrModalTransactionId) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setQrModalTransactionId(null);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [qrModalTransactionId]);

  const [stock, setStock] = useState<
    Array<{
      consumableTypeId: string;
      consumableTypeName: string;
      quantity: number;
      lowStockThreshold: number;
      isLowStock: boolean;
    }>
  >([]);

  // Fetch products for transaction creation
  useEffect(() => {
    const fetchProducts = async () => {
      if (!authState?.outletId) return;

      try {
        const response = await apiClient.products.getByOutlet(authState.outletId);

        if (response.success && response.data) {
          setProducts(response.data);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };

    fetchProducts();
  }, [authState?.outletId]);

  // Fetch stock for low-stock warning
  useEffect(() => {
    const fetchStock = async () => {
      if (!authState?.outletId) return;

      try {
        const response = await apiClient.outlets.getStock(authState.outletId);
        if (response.success && response.data) {
          setStock(response.data);
        }
      } catch (err) {
        console.error('Error fetching stock:', err);
      }
    };

    fetchStock();
  }, [authState?.outletId]);

  const pendingCashOrders = transactions.filter(
    (t) => t.payment.status === 'pending' && t.payment.method === 'cash'
  );

  // Resolve product name for table: use API productName only if it's a string; else lookup from outlet products; else productId
  const productNameById = new Map(products.map((p) => [p.productId, p.name]));
  const getProductDisplayName = (t: Transaction) => {
    const fromApi = typeof t.productName === 'string' && t.productName ? t.productName : null;
    const main = fromApi ?? productNameById.get(t.productId) ?? t.productId;
    if (t.addons && t.addons.length > 0) {
      const addonCount = t.addons.reduce((s, a) => s + a.quantity, 0);
      return `${main} + ${addonCount} add-on${addonCount > 1 ? 's' : ''}`;
    }
    return main;
  };

  const getTransactionTotalAmount = (t: Transaction) =>
    (t.payment?.amount ?? 0) + (t.addons?.reduce((s, a) => s + a.totalPrice, 0) ?? 0);

  const formatPaperType = (paperType?: string) => {
    if (!paperType) return '-';
    if (paperType === 'collage_4x6') return '4x6 Collage';
    if (paperType === 'strip') return 'Strip';
    return paperType.replace(/_/g, ' ');
  };

  const handleApprovePayment = async (transactionId: string) => {
    try {
      setApprovingId(transactionId);
      const response = await apiClient.transactions.approvePayment(transactionId);

      if (response.success) {
        await fetchTransactions();
      } else {
        alert(response.error?.message || 'Failed to approve payment');
      }
    } catch (err) {
      console.error('Error approving payment:', err);
      alert('Failed to approve payment');
    } finally {
      setApprovingId(null);
    }
  };

  const handleCancelPayment = async (transactionId: string) => {
    try {
      setCancellingId(transactionId);
      const response = await apiClient.transactions.cancelPayment(transactionId);

      if (response.success) {
        await fetchTransactions();
      } else {
        alert(response.error?.message || 'Failed to cancel payment');
      }
    } catch (err) {
      console.error('Error cancelling payment:', err);
      alert('Failed to cancel payment');
    } finally {
      setCancellingId(null);
    }
  };

  const handleMarkPrintDone = async (transactionId: string) => {
    try {
      setMarkingPrintDoneId(transactionId);
      const response = await apiClient.transactions.updatePrintStatus(transactionId, 'completed');

      if (response.success) {
        await fetchTransactions();
      } else {
        alert(response.error?.message || 'Failed to mark print as done');
      }
    } catch (err) {
      console.error('Error marking print as done:', err);
      alert('Failed to mark print as done');
    } finally {
      setMarkingPrintDoneId(null);
    }
  };

  const handleCopyTransactionId = async (transactionId: string) => {
    try {
      await navigator.clipboard.writeText(transactionId);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = transactionId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleShowQR = async (transactionId: string) => {
    setQrModalTransactionId(transactionId);
    setQrCodeUrl(null);
    try {
      const url = await QRCode.toDataURL(transactionId, {
        width: 320,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
      setQrCodeUrl(url);
    } catch (err) {
      console.error('Failed to generate QR code:', err);
    }
  };

  const handleOpenAddAddons = (transaction: Transaction) => {
    setAddAddonsTransaction(transaction);
    setShowAddAddonsModal(true);
  };

  const handleCloseAddAddons = () => {
    setAddAddonsTransaction(null);
    setShowAddAddonsModal(false);
  };

  const handleAddAddons = async (
    transactionId: string,
    items: Array<{ productId: string; quantity: number }>
  ) => {
    try {
      setIsAddingAddons(true);
      const response = await apiClient.transactions.addAddons(transactionId, items);
      if (response.success) {
        await fetchTransactions();
        handleCloseAddAddons();
        alert('Add-ons added successfully!');
      } else {
        alert(response.error?.message || 'Failed to add add-ons');
      }
    } catch (err) {
      console.error('Error adding add-ons:', err);
      alert('Failed to add add-ons');
    } finally {
      setIsAddingAddons(false);
    }
  };

  const [removingAddonId, setRemovingAddonId] = useState<string | null>(null);
  const handleRemoveAddon = async (transactionId: string, addonId: string) => {
    try {
      setRemovingAddonId(addonId);
      const response = await apiClient.transactions.removeAddon(transactionId, addonId);
      if (response.success && response.data) {
        setAddAddonsTransaction(response.data);
        await fetchTransactions();
      } else {
        alert(response.error?.message || 'Failed to remove add-on');
      }
    } catch (err) {
      console.error('Error removing add-on:', err);
      alert('Failed to remove add-on');
    } finally {
      setRemovingAddonId(null);
    }
  };

  const handleCreateTransaction = async (
    productId: string,
    customer: CustomerInfo,
    voucherCode?: string
  ) => {
    if (!authState?.outletId) return;

    try {
      setIsCreatingTransaction(true);

      const response = await apiClient.transactions.createOrder({
        outletId: authState.outletId,
        productId,
        customer,
        paymentMethod: 'cash',
        markAsPaid: true,
        voucherCode,
      });

      if (response.success) {
        await fetchTransactions();
        setShowNewTransactionModal(false);
        alert('Transaction created successfully!');
      } else {
        alert(response.error?.message || 'Failed to create transaction');
      }
    } catch (err) {
      console.error('Error creating transaction:', err);
      alert('Failed to create transaction');
    } finally {
      setIsCreatingTransaction(false);
    }
  };

  // Limit date range: start date at most 2 weeks ago, end date not in the future
  const today = new Date();
  const minStartDate = new Date(today);
  minStartDate.setDate(minStartDate.getDate() - 14);
  const minStartDateStr = minStartDate.toISOString().slice(0, 10);
  const maxEndDateStr = today.toISOString().slice(0, 10);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
    }).format(amount);
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-6">
      {/* Header */}
      <div className="w-full mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Cashier Dashboard
              </h1>
              <div className="flex items-center gap-4 text-gray-600">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🏪</span>
                  <span className="font-medium">{outlet?.name || 'Loading...'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📍</span>
                  <span>{outlet?.location || 'Loading...'}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/cashier/daily-summary')}
                className="px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg text-sm font-medium transition-colors border border-purple-200"
              >
                Daily Summary
              </button>
              <button
                type="button"
                onClick={() => setShowLogoutModal(true)}
                className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm font-medium transition-colors border border-red-200"
              >
                Logout
              </button>
              <button
                type="button"
                onClick={() => navigate('/settings')}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-sm font-medium transition-colors"
              >
                Settings
              </button>
              <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-800 rounded-lg font-medium">
                <span className="text-2xl">👤</span>
                <span>Cashier Role</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Low Stock Warning */}
      {stock.filter((s) => s.isLowStock).length > 0 && (
        <div className="w-full mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg shadow-md p-4">
            <h2 className="text-lg font-bold text-red-900 mb-2">Low Stock Alert</h2>
            <ul className="list-disc list-inside text-red-800">
              {stock
                .filter((s) => s.isLowStock)
                .map((s) => (
                  <li key={s.consumableTypeId}>
                    {s.consumableTypeName}: {s.quantity} (threshold: {s.lowStockThreshold})
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}

      {/* Pending Cash Orders */}
      {pendingCashOrders.length > 0 && (
        <div className="w-full mb-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-amber-900 mb-4">
              Pending Cash Orders ({pendingCashOrders.length})
            </h2>
            <div className="space-y-3">
              {pendingCashOrders.map((txn) => (
                <div
                  key={txn.transactionId}
                  className="flex items-center justify-between bg-white rounded-lg p-4 border border-amber-100"
                >
                  <div>
                    <div className="font-mono font-medium text-sm flex items-center">
                      {txn.transactionId}
                      <CopyTransactionIdButton onClick={() => handleCopyTransactionId(txn.transactionId)} />
                    </div>
                    <div className="text-gray-600 mt-1">
                      {getProductDisplayName(txn)} · {txn.customer.name} – {txn.customer.phone} – {formatCurrency(txn.payment.amount)}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button
                      onClick={() => handleShowQR(txn.transactionId)}
                      className="px-3 py-2 text-sm font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors border border-purple-200"
                    >
                      Show QR
                    </button>
                    <button
                      onClick={() => handleApprovePayment(txn.transactionId)}
                      disabled={approvingId === txn.transactionId || cancellingId === txn.transactionId}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {approvingId === txn.transactionId ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleCancelPayment(txn.transactionId)}
                      disabled={approvingId === txn.transactionId || cancellingId === txn.transactionId}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {cancellingId === txn.transactionId ? 'Cancelling...' : 'Cancel'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Transactions List */}
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h2 className="text-2xl font-bold text-gray-900">Transactions</h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fetchTransactions()}
                  disabled={isLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                  onClick={() => setShowNewTransactionModal(true)}
                >
                  + New Transaction
                </button>
              </div>
            </div>

            {/* Search and filters */}
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search (order ID, name, phone, email)
                </label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Status
                </label>
                <select
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Capture Status
                </label>
                <select
                  value={captureStatusFilter}
                  onChange={(e) => setCaptureStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Edit Status
                </label>
                <select
                  value={editStatusFilter}
                  onChange={(e) => setEditStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Print Status
                </label>
                <select
                  value={printStatusFilter}
                  onChange={(e) => setPrintStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="queued">Queued</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={minStartDateStr}
                  max={maxEndDateStr}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || minStartDateStr}
                  max={maxEndDateStr}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          {isLoading && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4 animate-bounce">⏳</div>
              <div className="text-xl text-gray-600">Loading transactions...</div>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⚠️</div>
              <div className="text-xl text-red-600 mb-2">Error</div>
              <div className="text-gray-600">{error}</div>
            </div>
          )}

          {!isLoading && !error && transactions.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <div className="text-xl text-gray-600">No transactions found</div>
              <div className="text-gray-500 mt-2">
                Transactions for this outlet will appear here
              </div>
            </div>
          )}

          {!isLoading && !error && transactions.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Transaction ID
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Customer
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Product
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Paper / Prints
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Amount
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Payment
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Capture
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Edit
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Print
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Actions
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr
                      key={transaction.transactionId}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4 font-mono text-sm">
                        <div className="flex flex-col gap-1.5">
                          <span className="inline-flex items-center">
                            {transaction.transactionId}
                            <CopyTransactionIdButton onClick={() => handleCopyTransactionId(transaction.transactionId)} />
                          </span>
                          <button
                            type="button"
                            onClick={() => handleShowQR(transaction.transactionId)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-700 bg-white border-2 border-purple-500 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer"
                          >
                            Show QR
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium">{transaction.customer.name}</div>
                        <div className="text-sm text-gray-500">
                          {transaction.customer.phone} · {transaction.customer.email}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {getProductDisplayName(transaction)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        <div>{formatPaperType(transaction.productPaperType)}</div>
                        <div className="text-gray-500">
                          {transaction.productPrintCount != null
                            ? `${transaction.productPrintCount} print${transaction.productPrintCount !== 1 ? 's' : ''}`
                            : '-'}
                        </div>
                      </td>
                      <td className="py-3 px-4 font-semibold">
                        {formatCurrency(getTransactionTotalAmount(transaction))}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                            transaction.payment.status
                          )}`}
                        >
                          {transaction.payment.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                            transaction.captureStatus
                          )}`}
                        >
                          {transaction.captureStatus}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                            transaction.editStatus
                          )}`}
                        >
                          {transaction.editStatus}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1.5">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                              transaction.printStatus
                            )}`}
                          >
                            {transaction.printStatus}
                          </span>
                          {transaction.printStatus === 'queued' && (
                            <button
                              type="button"
                              onClick={() => handleMarkPrintDone(transaction.transactionId)}
                              disabled={markingPrintDoneId === transaction.transactionId}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-white border-2 border-green-500 rounded-lg hover:bg-green-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {markingPrintDoneId === transaction.transactionId ? 'Marking...' : 'Mark as done'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {transaction.payment.status === 'completed' && (
                          <button
                            type="button"
                            onClick={() => handleOpenAddAddons(transaction)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-white border-2 border-green-500 rounded-lg hover:bg-green-50 transition-colors cursor-pointer"
                          >
                            Add add-ons
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDate(transaction.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      {qrModalTransactionId && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setQrModalTransactionId(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Transaction QR Code</h3>
              <button
                type="button"
                onClick={() => setQrModalTransactionId(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-600 font-mono mb-4 break-all">{qrModalTransactionId}</p>
            {qrCodeUrl ? (
              <div className="bg-white p-4 rounded-xl border border-gray-200 inline-block mx-auto flex justify-center">
                <img src={qrCodeUrl} alt="Transaction QR Code" className="w-64 h-64" />
              </div>
            ) : (
              <div className="w-64 h-64 flex items-center justify-center bg-gray-100 rounded-xl">
                <span className="text-gray-500">Generating...</span>
              </div>
            )}
            <p className="text-sm text-gray-500 mt-4 text-center">
              Scan at Capture
            </p>
          </div>
        </div>
      )}

      {/* New Transaction Modal */}
      {showNewTransactionModal && authState?.outletId && (
        <NewTransactionModal
          outletId={authState.outletId}
          products={products}
          onClose={() => setShowNewTransactionModal(false)}
          onCreate={handleCreateTransaction}
          isCreating={isCreatingTransaction}
        />
      )}

      {/* Add Add-ons Modal */}
      {showAddAddonsModal && addAddonsTransaction && (
        <AddAddonsModal
          transaction={addAddonsTransaction}
          products={products}
          onClose={handleCloseAddAddons}
          onSubmit={handleAddAddons}
          onRemoveAddon={handleRemoveAddon}
          isSubmitting={isAddingAddons}
          removingAddonId={removingAddonId}
        />
      )}

      {/* Logout Modal */}
      {showLogoutModal && authState?.outletId && (
        <LogoutModal
          outletId={authState.outletId}
          terminalName="Cashier terminal"
          onClose={() => setShowLogoutModal(false)}
          onLogout={async () => {
            // Clear authentication and navigate back to auth screen after successful logout
            await clearAuthentication();
            setShowLogoutModal(false);
            navigate('/auth');
          }}
        />
      )}
    </div>
  );
}

interface NewTransactionModalProps {
  outletId: string;
  products: Product[];
  onClose: () => void;
  onCreate: (productId: string, customer: CustomerInfo, voucherCode?: string) => void;
  isCreating: boolean;
}

function NewTransactionModal({
  outletId,
  products,
  onClose,
  onCreate,
  isCreating,
}: NewTransactionModalProps) {
  const [selectedProductId, setSelectedProductId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerInstagram, setCustomerInstagram] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherDiscount, setVoucherDiscount] = useState<number | null>(null);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const [isValidatingVoucher, setIsValidatingVoucher] = useState(false);

  const selectedProduct = products.find((p) => p.productId === selectedProductId);
  const productPrice = selectedProduct?.basePrice ?? 0;
  const finalPrice = Math.max(0, productPrice - (voucherDiscount ?? 0));

  const handleVoucherBlur = async () => {
    if (!voucherCode.trim() || !selectedProductId) {
      setVoucherDiscount(null);
      setVoucherError(null);
      return;
    }
    setIsValidatingVoucher(true);
    setVoucherError(null);
    setVoucherDiscount(null);
    try {
      const response = await apiClient.vouchers.validate({
        code: voucherCode.trim(),
        productId: selectedProductId,
        outletId,
        customerPhone: customerPhone || undefined,
        customerEmail: customerEmail || undefined,
      });
      if (response.success && response.data?.valid && response.data?.discountAmount != null) {
        setVoucherDiscount(response.data.discountAmount);
        setVoucherError(null);
      } else {
        setVoucherDiscount(null);
        setVoucherError(response.data?.message || 'Invalid voucher');
      }
    } catch {
      setVoucherDiscount(null);
      setVoucherError('Failed to validate voucher');
    } finally {
      setIsValidatingVoucher(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProductId || !customerName || !customerPhone || !customerEmail) {
      alert('Please fill in all required fields');
      return;
    }

    const validVoucherCode =
      voucherCode.trim() && !voucherError && voucherDiscount != null ? voucherCode.trim() : undefined;

    onCreate(
      selectedProductId,
      {
        name: customerName,
        phone: customerPhone,
        email: customerEmail,
        instagram: customerInstagram || undefined,
      },
      validVoucherCode
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">New Transaction</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isCreating}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Product Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product *
              </label>
              <select
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
                disabled={isCreating}
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.productId} value={product.productId}>
                    {product.name} - {new Intl.NumberFormat('id-ID', {
                      style: 'currency',
                      currency: 'IDR',
                    }).format(product.basePrice)}
                  </option>
                ))}
              </select>
            </div>

            {/* Customer Information */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Customer Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Customer name"
                    required
                    disabled={isCreating}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="+62 812 3456 7890"
                    required
                    disabled={isCreating}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="customer@example.com"
                    required
                    disabled={isCreating}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instagram (optional)
                  </label>
                  <input
                    type="text"
                    value={customerInstagram}
                    onChange={(e) => setCustomerInstagram(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="@username"
                    disabled={isCreating}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Voucher Code (optional)
                  </label>
                  <input
                    type="text"
                    value={voucherCode}
                    onChange={(e) => {
                      setVoucherCode(e.target.value);
                      setVoucherError(null);
                      setVoucherDiscount(null);
                    }}
                    onBlur={handleVoucherBlur}
                    disabled={isCreating || !selectedProductId || isValidatingVoucher}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter voucher code"
                  />
                  {isValidatingVoucher && <p className="mt-1 text-sm text-gray-500">Validating...</p>}
                  {voucherError && <p className="mt-1 text-sm text-red-600">{voucherError}</p>}
                  {voucherDiscount != null && voucherDiscount > 0 && (
                    <p className="mt-1 text-sm text-green-600">
                      Discount: -{new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(voucherDiscount)}. Final: {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(finalPrice)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isCreating}
              >
                {isCreating ? 'Creating...' : 'Create Transaction'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

interface AddAddonsModalProps {
  transaction: Transaction;
  products: Product[];
  onClose: () => void;
  onSubmit: (transactionId: string, items: Array<{ productId: string; quantity: number }>) => void | Promise<void>;
  onRemoveAddon: (transactionId: string, addonId: string) => void | Promise<void>;
  isSubmitting: boolean;
  removingAddonId: string | null;
}

function AddAddonsModal({
  transaction,
  products,
  onClose,
  onSubmit,
  onRemoveAddon,
  isSubmitting,
  removingAddonId,
}: AddAddonsModalProps) {
  const addonProducts = products.filter((p) => p.productType === 'addon');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const existingAddons = transaction.addons ?? [];

  const handleQuantityChange = (productId: string, value: number) => {
    setQuantities((prev) => ({ ...prev, [productId]: Math.max(0, value) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const items = addonProducts
      .filter((p) => (quantities[p.productId] ?? 0) > 0)
      .map((p) => ({ productId: p.productId, quantity: quantities[p.productId] ?? 1 }));
    if (items.length === 0) {
      alert('Please select at least one add-on with quantity > 0');
      return;
    }
    onSubmit(transaction.transactionId, items);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Add Add-ons</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isSubmitting}
            >
              ×
            </button>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 font-mono">{transaction.transactionId}</p>
            <p className="font-medium text-gray-900 mt-1">
              {transaction.customer.name} · {transaction.productName ?? transaction.productId}
            </p>
          </div>

          {existingAddons.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Current add-ons</label>
              <div className="space-y-2">
                {existingAddons.map((addon) => (
                  <div
                    key={addon.transactionAddonId}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50"
                  >
                    <div>
                      <span className="font-medium text-gray-900">{addon.productName ?? 'Add-on'}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        ×{addon.quantity} · {formatCurrency(addon.totalPrice)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveAddon(transaction.transactionId, addon.transactionAddonId)}
                      disabled={isSubmitting || removingAddonId === addon.transactionAddonId}
                      className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {removingAddonId === addon.transactionAddonId ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {addonProducts.length === 0 ? (
            <p className="text-gray-600 mb-6">No add-on products available.</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Add-on products</label>
                <div className="space-y-3">
                  {addonProducts.map((product) => (
                    <div
                      key={product.productId}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                    >
                      <div>
                        <span className="font-medium text-gray-900">{product.name}</span>
                        <span className="ml-2 text-sm text-gray-500">{formatCurrency(product.basePrice)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          value={quantities[product.productId] ?? 0}
                          onChange={(e) => handleQuantityChange(product.productId, parseInt(e.target.value, 10) || 0)}
                          className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-center"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Adding...' : 'Add Add-ons'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
